import type {
  HostDirectoryService,
  PeerDescriptor,
  BroadcastDescriptor,
  ChannelDescriptor,
  DirectoryChangeEvent,
  HostBroadcastService,
  Result,
} from './types.js';
import type { CollabEnvelope } from '../types/envelope.js';
import { parseHtsUri } from '../validation/uri-validation.js';

export function createBroadcastDescriptor(opts: {
  nodeId: string;
  kind: 'video' | 'screenShare' | 'whiteboard' | 'broadcast' | 'audio';
  topic?: string;
}): BroadcastDescriptor {
  const sanitizedKind = opts.kind.toLowerCase();
  const uri = parseHtsUri(`hts://mesh/broadcast/${opts.nodeId}-${sanitizedKind}`).canonical;
  
  return {
    uri,
    pluginId: 'broadcast',
    topic: opts.topic ?? sanitizedKind,
    publisherNodeId: opts.nodeId,
    lastActive: Date.now(),
  };
}

export class DirectoryService implements HostDirectoryService {
  private peers = new Map<string, PeerDescriptor>();
  private broadcasts = new Map<string, BroadcastDescriptor>();
  private channels = new Map<string, ChannelDescriptor>();
  private listeners = new Set<(event: DirectoryChangeEvent) => void>();

  constructor(
    private readonly localNodeId: string,
    private readonly broadcastService: HostBroadcastService
  ) {
    this.hydrateFromStorage();
    this.broadcastService.subscribe('hts://mesh/directory/announcements', (envelope) => this.handleIncomingAnnouncement(envelope));
  }

  private hydrateFromStorage(): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      // 1. Load registered members list
      const savedMembers = localStorage.getItem('mesh_registered_members');
      if (savedMembers) {
        const parsed = JSON.parse(savedMembers);
        if (Array.isArray(parsed)) {
          for (const m of parsed) {
            if (m && m.nodeId && !this.peers.has(m.nodeId)) {
              this.peers.set(m.nodeId, {
                nodeId: m.nodeId,
                displayName: m.displayName || `Peer (${m.nodeId.substring(0, 8)})`,
                hnsHandle: m.hnsAlias || `hns://${m.nodeId}.mesh`,
                lastSeen: m.lastSeen || Date.now(),
                status: m.status || 'online',
                links: ['broadcast_channel'],
              });
            }
          }
        }
      }

      // 2. Load any profile_data keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('profile_data_')) {
          const peerId = key.replace('profile_data_', '');
          const val = localStorage.getItem(key);
          if (val && !this.peers.has(peerId)) {
            try {
              const p = JSON.parse(val);
              this.peers.set(peerId, {
                nodeId: peerId,
                displayName: p.displayName || `Peer (${peerId.substring(0, 8)})`,
                hnsHandle: p.hnsAlias || `hns://${peerId}.mesh`,
                lastSeen: Date.now(),
                status: 'online',
                links: ['broadcast_channel'],
              });
            } catch {}
          }
        }
      }
    } catch (e) {
      console.warn('Failed to hydrate DirectoryService from localStorage', e);
    }
  }

  private persistMember(peer: PeerDescriptor): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      let members: any[] = [];
      const saved = localStorage.getItem('mesh_registered_members');
      if (saved) {
        try {
          members = JSON.parse(saved);
        } catch {}
      }
      const idx = members.findIndex((m) => m.nodeId === peer.nodeId);
      const entry = {
        nodeId: peer.nodeId,
        displayName: peer.displayName,
        hnsAlias: peer.hnsHandle,
        status: peer.status,
        lastSeen: peer.lastSeen,
      };
      if (idx >= 0) {
        members[idx] = entry;
      } else {
        members.push(entry);
      }
      localStorage.setItem('mesh_registered_members', JSON.stringify(members));
    } catch {}
  }

  async listBroadcasts(): Promise<BroadcastDescriptor[]> {
    return Array.from(this.broadcasts.values()).map((b) => ({ ...b }));
  }

  getLocalNodeId(): string {
    return this.localNodeId;
  }

  async listChannels(): Promise<ChannelDescriptor[]> {
    return Array.from(this.channels.values()).map((c) => ({ ...c }));
  }

  async listPeers(): Promise<PeerDescriptor[]> {
    return Array.from(this.peers.values()).map((p) => ({ ...p }));
  }

  getPeer(nodeId: string): PeerDescriptor | undefined {
    const peer = this.peers.get(nodeId);
    return peer ? { ...peer } : undefined;
  }

  recordHeartbeat(peer: PeerDescriptor): void {
    const updated: PeerDescriptor = {
      ...peer,
      status: peer.status || 'online',
      links: [...peer.links],
    };
    this.peers.set(peer.nodeId, updated);
    this.persistMember(updated);
    this.emitChange({ type: 'PEER_UPDATED', peer: updated });

    // Announce heartbeat to mesh network
    this.broadcastService.publish('hts://mesh/directory/announcements', {
      action: 'PEER_HEARTBEAT',
      peer: updated,
    }).catch(() => {});
  }

  async registerBroadcast(broadcast: BroadcastDescriptor): Promise<Result<void>> {
    if (broadcast.publisherNodeId !== this.localNodeId) {
      return { ok: false, error: { type: 'NOT_AUTHORIZED', code: '403', message: 'Cannot register broadcast for another node' } };
    }

    const exists = this.broadcasts.has(broadcast.uri);
    const updated = { ...broadcast };
    this.broadcasts.set(broadcast.uri, updated);
    this.emitChange({ type: exists ? 'BROADCAST_UPDATED' : 'BROADCAST_ADDED', broadcast: updated });

    await this.broadcastService.publish('hts://mesh/directory/announcements', {
      action: exists ? 'BROADCAST_UPDATED' : 'BROADCAST_ADDED',
      broadcast: updated,
    });
    return { ok: true, value: undefined };
  }

  async unregisterBroadcast(uri: string): Promise<Result<void>> {
    const existing = this.broadcasts.get(uri);
    if (!existing) {
      return { ok: true, value: undefined };
    }
    
    if (existing.publisherNodeId !== this.localNodeId) {
      return { ok: false, error: { type: 'NOT_AUTHORIZED', code: '403', message: 'Cannot unregister broadcast for another node' } };
    }

    this.broadcasts.delete(uri);
    this.emitChange({ type: 'BROADCAST_REMOVED', uri, publisherNodeId: this.localNodeId });

    await this.broadcastService.publish('hts://mesh/directory/announcements', {
      action: 'BROADCAST_REMOVED',
      uri,
      publisherNodeId: this.localNodeId,
    });
    return { ok: true, value: undefined };
  }

  recordChannel(channel: ChannelDescriptor): void {
    const updated = { ...channel };
    this.channels.set(channel.uri, updated);
    this.emitChange({ type: 'CHANNEL_UPDATED', channel: updated });
  }

  pruneExpiredPeers(nowMs: number, ttlMs: number): void {
    const evicted: string[] = [];
    for (const [nodeId, peer] of this.peers.entries()) {
      // Don't evict local node
      if (nodeId === this.localNodeId) continue;

      const age = nowMs - peer.lastSeen;
      if (age > ttlMs * 2) {
        this.peers.delete(nodeId);
        evicted.push(nodeId);
      } else if (age > ttlMs && peer.status !== 'stale') {
        const stalePeer: PeerDescriptor = { ...peer, status: 'stale' };
        this.peers.set(nodeId, stalePeer);
        this.emitChange({ type: 'PEER_STALE', peer: stalePeer });
      }
    }

    if (evicted.length > 0) {
      this.emitChange({ type: 'PEERS_EVICTED', nodeIds: evicted });
    }
  }

  handleIncomingAnnouncement(envelope: CollabEnvelope<unknown>): void {
    const payload = envelope.payload as any;
    if (!payload || !payload.action) return;

    switch (payload.action) {
      case 'PEER_HEARTBEAT':
      case 'PEER_ADDED':
      case 'PEER_UPDATED': {
        const peer = payload.peer as PeerDescriptor;
        if (!peer || !peer.nodeId) return;
        if (peer.nodeId === this.localNodeId || envelope.senderId === this.localNodeId) return;
        
        const exists = this.peers.has(peer.nodeId);
        const updated: PeerDescriptor = {
          ...peer,
          lastSeen: peer.lastSeen || Date.now(),
          status: peer.status || 'online',
        };
        this.peers.set(peer.nodeId, updated);
        this.persistMember(updated);
        this.emitChange({ type: exists ? 'PEER_UPDATED' : 'PEER_ADDED', peer: updated });
        break;
      }
      case 'BROADCAST_ADDED':
      case 'BROADCAST_UPDATED': {
        const broadcast = payload.broadcast as BroadcastDescriptor;
        if (!broadcast) return;
        if (broadcast.publisherNodeId === this.localNodeId) return;
        
        const exists = this.broadcasts.has(broadcast.uri);
        this.broadcasts.set(broadcast.uri, broadcast);
        this.emitChange({ type: exists ? 'BROADCAST_UPDATED' : 'BROADCAST_ADDED', broadcast });
        break;
      }
      case 'BROADCAST_REMOVED': {
        const { uri, publisherNodeId } = payload;
        if (!uri) return;
        if (publisherNodeId === this.localNodeId) return;
        
        const existing = this.broadcasts.get(uri);
        if (existing) {
          this.broadcasts.delete(uri);
          this.emitChange({ type: 'BROADCAST_REMOVED', uri, publisherNodeId });
        }
        break;
      }
    }
  }

  onDirectoryChange(callback: (event: DirectoryChangeEvent) => void): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  private emitChange(event: DirectoryChangeEvent): void {
    for (const listener of this.listeners) {
      listener(event);
    }
  }
}
